import { useSearchParams } from 'next/navigation';
import { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { faCheckCircle, faClock } from '@fortawesome/free-regular-svg-icons';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { LayoutContext } from '@/contexts/LayoutContext';
import { useEnv } from '@/env/client';
import { useFormApi } from '@lamesarv-sdk/hooks';
import { useGetInventoryItem } from '@lamesarv-sdk/inventory';
import { navigate } from '@lamesarv-sdk/tools';
import { useDynamicsForms } from '@/hooks/dynamicsForms';
import { isFormDynamicsLocationEnabled } from '@/tools/forms';
import { useDevice } from '@/hooks/useDevice';
import { parsePhone } from '@/tools/dynamics';
import { withSuspense } from '@/tools/withSuspense';

import { RichText } from '@lamesarv-sdk/components';
import { CommonForm } from '@/components/CommonForm';
import { CommonHidden } from '@/components/CommonForm/CommonHidden';
import { CommonInput } from '@/components/CommonForm/CommonInput';
import { CommonLocationSelect } from '@/components/CommonForm/CommonLocationSelect';
import { CommonMaskInput } from '@/components/CommonForm/CommonMaskInput';
import { CommonTextarea } from '@/components/CommonForm/CommonTextarea';

import { BaseCaptchaError, BaseSubmit } from '../base';
import { IFormSchema, validationSchema } from './schema';

import { IInventory, InventoryField } from '@lamesarv-sdk/types';
import { DynamicsLeadSource, DynamicsLeadType, IDynamicsFormMapping, IDynamicsOptionSet } from '@/types/dynamicsForms';
import { IFormProps, IFormResetterProps } from '../types';

import * as FormClasses from '../classes';

const emailTypePrefix: { [key: string]: string } = {
  'best-price': 'Best Price for',
  'check-availability': 'Check availability for',
  'more-info': 'More Info for',
};

const FormResetter = (props: IFormResetterProps) => {
  const { reset } = useFormContext();

  useEffect(() => {
    if (!props.wasSuccessful) return;

    reset();
    reset({ phone: '', mobile: '' });

    if (props.callback) {
      props.callback();
    }

    if (props.redirect) {
      navigate(props.redirect);
    }
  }, [props.wasSuccessful]);

  return null;
};

export interface IFormContactInfoProps extends IFormProps {
  locationsOptionSet?: IDynamicsOptionSet;
  customLeadType?: string;
}

export const FormContactInfo = withSuspense((props: IFormContactInfoProps) => {
  const { context: layoutContext } = useContext(LayoutContext);
  const { fetchData: getDevice } = useDevice({ metadata: true, skipFetch: true });
  const searchParams = useSearchParams();
  const { env } = useEnv();

  const [stockNumber, setStockNumber] = useState<string | undefined>(undefined);
  const [emailType, setEmailType] = useState<string | undefined>(undefined);
  const [details, setDetails] = useState<string[]>([]);

  const [formInventory, setFormInventory] = useState<IInventory | undefined>(undefined);
  const [formTitle, setFormTitle] = useState<string>('');
  const [formCustomComments, setFormCustomComments] = useState<string>('');

  const { fetchItem } = useGetInventoryItem({ skipFetch: true });

  const [loadingLegacy, submittedLegacy, errorLegacy, sendFormLegacy] = useFormApi();
  const { loading, submitted, error, sendForm } = useDynamicsForms();

  const onSubmit = async (formInput: IFormSchema, metadata?: Record<string, unknown>) => {
    const locationItem = layoutContext.inventoryLocations.find((location) =>
      location.aliases.includes(formInput.location.toLowerCase().trim()),
    );

    let zipCode = locationItem?.address?.trim().split(' ').pop() || '';
    if (zipCode.length !== 5) zipCode = '';

    const formMappings: IDynamicsFormMapping[] = [
      {
        DataverseFieldName: 'lmrv_leadtype',
        DataverseFieldValue: String(DynamicsLeadType.seller),
      },
      {
        DataverseFieldName: 'leadsourcecode',
        DataverseFieldValue: String(DynamicsLeadSource.website),
      },
      { DataverseFieldName: 'lmrv_stocknumber', DataverseFieldValue: stockNumber ? stockNumber.toLowerCase() : '' },
      { FormFieldName: 'firstName', DataverseFieldName: 'firstname' },
      { FormFieldName: 'lastName', DataverseFieldName: 'lastname' },
      { FormFieldName: 'email', DataverseFieldName: 'emailaddress1' },
      {
        DataverseFieldName: 'address1_postalcode',
        DataverseFieldValue: zipCode,
      },
      { FormFieldName: 'comments', DataverseFieldName: 'lmrv_comments' },
      { DataverseFieldName: 'telephone2', DataverseFieldValue: parsePhone(formInput.phone) },
      { DataverseFieldName: 'mobilephone', DataverseFieldValue: parsePhone(formInput.mobile) },
      {
        DataverseFieldName: 'lmrv_dealership',
        DataverseFieldValue:
          String(props.locationsOptionSet.find((item) => item.Label === formInput.location)?.DataverseValue) || '',
      },
      {
        DataverseFieldName: 'lmrv_url',
        DataverseFieldValue: typeof location !== 'undefined' ? location?.href : '',
      },
      {
        DataverseFieldName: 'crbdb_segment',
        DataverseFieldValue: props.dynamics_segment,
      },
    ];

    if (formCustomComments) {
      formInput.comments = formCustomComments + '\n\n' + formInput.comments;
    }

    if (env.ENABLE_FORMS_CAPTURE_IP) {
      const device = await getDevice();
      formInput.ip = device.ip;
      formInput.user_agent = device.userAgent;
      formInput.platform = device.platform;
      formMappings.push(
        { DataverseFieldName: 'lmrv_ip', DataverseFieldValue: device.ip },
        { DataverseFieldName: 'lmrv_useragent', DataverseFieldValue: device.userAgent },
        { DataverseFieldName: 'lmrv_platform', DataverseFieldValue: device.platform },
      );
    }

    if (env.ENABLE_FORMS_CAPTURE_GEO && !!layoutContext.geoLocation) {
      formInput.geo_coordinates = layoutContext.geoLocation.join(',');
      formMappings.push({
        DataverseFieldName: 'lmrv_geocoordinates',
        DataverseFieldValue: layoutContext.geoLocation.join(','),
      });
    }

    if (env.ENABLE_FORMS_CAPTURE_CAPTCHA_SCORE && env.ENABLE_FORMS_CAPTCHA) {
      formInput.captcha_score = metadata?.captchaScore ? Number(metadata?.captchaScore) : undefined;
      formInput.captcha_threshold = metadata?.captchaThreshold ? Number(metadata?.captchaThreshold) : undefined;
      formMappings.push(
        {
          DataverseFieldName: 'lmrv_captchascore',
          DataverseFieldValue: formInput.captcha_score,
        },
        {
          DataverseFieldName: 'lmrv_captchathreshold',
          DataverseFieldValue: formInput.captcha_threshold,
        },
      );
    }

    if (env.ENABLE_FORMS_LEGACY) {
      const commentsWithPreferredLocation = `Preferred Location: ${formInput.location}\n${formInput.comments || ''}`;

      sendFormLegacy({
        ...formInput,
        comments: commentsWithPreferredLocation,
        zipCode,
        stockNumber: stockNumber ? stockNumber.toLowerCase() : undefined,
        emailType: emailType,
      });
    }

    if (env.ENABLE_FORMS_DYNAMICS && isFormDynamicsLocationEnabled(env, formInput.location)) {
      await sendForm({
        formId: `#${props.custom_id || 'formContactInfo'}`,
        mappings: formMappings,
        dynamicsFormId: env.DYNAMICS_FORM_ID_CONTACT_INFO,
      });
    }
  };

  const getInventory = async () => {
    if (!stockNumber) {
      return;
    }

    try {
      const inventoryItem: IInventory = await fetchItem({ inventoryId: stockNumber });
      setFormInventory(inventoryItem);
    } catch (err) {
      console.error('Inventory item not found', err);
      return;
    }
  };

  const getFormTitle = async () => {
    if (!formInventory) {
      return;
    }

    const titlePrefix = (!!emailType && emailTypePrefix[emailType]) || emailTypePrefix['more-info'];
    setFormTitle(`${titlePrefix} ${formInventory[InventoryField.title]}`);
  };

  const getFormComments = async () => {
    if (!formInventory) {
      return;
    }

    if (details.includes('rebate')) {
      setFormCustomComments(
        `Interested in more information on $${formInventory[InventoryField.rebate].amount.toLocaleString()} rebate\n\n`,
      );
    }
  };

  const _loading = loading || loadingLegacy || false;
  const _error = error || errorLegacy || false;
  const _submitted = submitted || submittedLegacy || false;

  const wasSuccessful = !_loading && !_error && _submitted;

  useEffect(() => {
    getInventory();
  }, [stockNumber]);

  useEffect(() => {
    getFormTitle();
  }, [formInventory, emailType]);

  useEffect(() => {
    getFormComments();
  }, [formInventory, details]);

  useEffect(() => {
    setStockNumber(searchParams.get('stockNumber') || undefined);
    setEmailType(searchParams.get('emailType') || undefined);
    setDetails(
      searchParams
        .get('details')
        ?.trim()
        ?.split(',')
        ?.map((item) => item.trim()) || [],
    );
  }, [searchParams]);

  // -- Prevent rendering if neither form system is enabled
  if (!env.ENABLE_FORMS_DYNAMICS && !env.ENABLE_FORMS_LEGACY) {
    return null;
  }

  return (
    <CommonForm
      id={props.custom_id || 'formContactInfo'}
      schema={validationSchema}
      onSubmit={onSubmit}
      className={twMerge('font-helvetica relative', props.classes)}
      isDisabled={_loading || wasSuccessful}
      tagGroup={props.data_tag_group || 'form'}
      tagItem={props.data_tag ? `${props.data_tag}--form-contact-info` : 'form-contact-info'}
      customCaptchaErrorMessage={() => <BaseCaptchaError />}
    >
      <CommonHidden name="formId" value="23" />
      <CommonHidden name="formType" value="more_info" />
      <CommonHidden name="leadType" value="seller" />
      <CommonHidden name="leadSource" value="lamesarv.com" />
      <FormResetter wasSuccessful={wasSuccessful} callback={props.successCallback} redirect={props.redirect_url} />
      {props.extract_header && (
        <div
          className={twMerge('w-full md:w-[720px] xl:w-[865px] mx-auto flex flex-col gap-4 p-1', props.classes_header)}
        >
          {formTitle ||
            (props.title && (
              <div className={twMerge('text-2xl text-sage-800 font-bold uppercase', props.classes_title)}>
                {formTitle || props.title}
              </div>
            ))}
          {props.description && (
            <div className={twMerge('text-lg text-sage-800', props.classes_description)}>
              <RichText content={props.description} />
            </div>
          )}
        </div>
      )}
      <div className={twMerge('w-full mx-auto flex flex-col gap-4 p-1 bg-white', props.classes_container)}>
        {!props.extract_header && (
          <div className={twMerge('flex flex-col gap-4', props.classes_header)}>
            {formTitle ||
              (props.title && (
                <div className={twMerge('text-2xl text-sage-800 font-bold uppercase', props.classes_title)}>
                  {formTitle || props.title}
                </div>
              ))}
            {props.description && (
              <div className={twMerge('text-lg text-sage-800', props.classes_description)}>
                <RichText content={props.description} />
              </div>
            )}
          </div>
        )}
        <div className={twMerge('flex flex-col', props.classes_content)}>
          <div className="flex flex-row flex-wrap -mx-2">
            {_loading && (
              <div
                className={
                  'flex items-center w-full bg-gray-100 border border-gray-500 text-gray-700 py-2 px-3 mb-4 mx-2 shadow-md'
                }
                role="alert"
              >
                <FontAwesomeIcon icon={faClock} className="w-8 mr-2" />
                <span>Sending...</span>
              </div>
            )}
            {wasSuccessful && (
              <div
                className={
                  'flex items-center w-full bg-green-100 border border-green-500 text-green-700 py-2 px-3 mb-4 mx-2 shadow-md'
                }
                role="alert"
              >
                <FontAwesomeIcon icon={faCheckCircle} className="w-8 mr-2" />
                <span
                  data-tag-group="form-success-label"
                  data-tag={
                    props.data_tag
                      ? `${props.data_tag}--form-contact-info-success-label`
                      : 'form-contact-info-success-label'
                  }
                >
                  Thank you for requesting more information.
                </span>
              </div>
            )}
            {!_loading && _error && (
              <div
                className={
                  'flex items-center w-full bg-red-100 border border-red-500 text-red-700 py-2 px-3 mb-4 mx-2 shadow-md'
                }
                role="alert"
              >
                <FontAwesomeIcon icon={faExclamationTriangle} className="w-8 mr-2" />
                <span>An error has occurred, please try again later or contact us using alternative methods.</span>
              </div>
            )}
            <div className="mb-2 px-2 w-full text-lg uppercase font-bold text-sage-700">Personal Information</div>
            <div className="my-2 px-2 w-full md:w-1/2">
              <CommonInput
                label="First Name"
                placeholder="First Name"
                name="firstName"
                isRequired
                classContainer="flex-1"
              />
            </div>
            <div className="my-2 px-2 w-full md:w-1/2">
              <CommonInput
                label="Last Name"
                placeholder="Last Name"
                name="lastName"
                isRequired
                classContainer="flex-1"
              />
            </div>
          </div>
          <div className="flex flex-row flex-wrap -mx-2">
            <div className="mt-3 mb-2 px-2 w-full text-lg uppercase font-bold text-sage-700">Contact Information</div>
            <div className="my-2 px-2 w-full md:w-1/3">
              <CommonMaskInput
                type="tel"
                mask="(999) 999-9999"
                label="Phone Number"
                placeholder="Phone"
                name="phone"
                classContainer="flex-1"
              />
            </div>
            <div className="my-2 px-2 w-full md:w-1/3">
              <CommonMaskInput
                type="tel"
                mask="(999) 999-9999"
                label="Mobile Phone"
                placeholder="Mobile Phone"
                name="mobile"
                classContainer="flex-1"
              />
            </div>
            <div className="my-2 px-2 w-full md:w-1/3">
              <CommonInput
                type="email"
                label="Email"
                placeholder="Email"
                name="email"
                isRequired
                classContainer="flex-1"
              />
            </div>
            <div className="my-2 px-2 w-full md:w-1/3">
              <CommonLocationSelect
                label="Select store location"
                classContainer="flex-1"
                locationsOptionSet={props.locationsOptionSet}
                isRequired
              />
            </div>
            <div className="my-2 px-2 w-full">
              <CommonTextarea label="Comments" name="comments" />
            </div>
          </div>
          <div className="flex flex-row">
            <div className="mt-2">
              <BaseSubmit {...props} formNameSlug="form-contact-info" styleClasses={FormClasses.buttonNormalClasses} />
            </div>
          </div>
        </div>
      </div>
    </CommonForm>
  );
});
